import { ACLOperationsEnum, CRUDOperationsEnum } from './constants'

export const operationCRUDToACL = (operation: CRUDOperationsEnum) => {
  if (operation === CRUDOperationsEnum.READ || operation === CRUDOperationsEnum.LIST) {
    return ACLOperationsEnum.READ
  }

  if (
    operation === CRUDOperationsEnum.CREATE ||
    operation === CRUDOperationsEnum.UPDATE ||
    operation === CRUDOperationsEnum.DELETE
  ) {
    return ACLOperationsEnum.WRITE
  }
}

/**
 * Checks if a value is a string
 */
export const isString = (value: unknown): value is string => {
  return typeof value === 'string'
}

/**
 * Checks if a value is a non-empty string
 * @param value - The value to check
 * @returns True if the value is a non-empty string, false otherwise
 */
export const isStringContent = (value: unknown): value is string => {
  return isString(value) && value.trim().length > 0
}

/**
 * Checks if a value is a function
 */
export const isFunction = (value: unknown): value is Function => {
  return typeof value === 'function'
}

/**
 * Type guard to check if a value is an object.
 *
 * @param value - The value to check
 * @returns True if the value is an object, false otherwise
 */
export const isObject = (value: unknown): value is Record<string, any> => {
  return value !== null && typeof value === 'object' && !Array.isArray(value)
}

/**
 * Prepares and returns fields in a consistent format based on the input type and primary key.
 *
 * Accepts:
 * - `string[]` – returned as-is when non-empty
 * - `string` – wrapped in a single-element array
 * - indexed plain object (`{"0":"id","1":"status"}`) – converted to a sorted array (compatibility
 *   shim for query-string parsers such as `qs` that exceed `arrayLimit` and serialize arrays as
 *   objects with numeric string keys)
 * - anything else / empty – falls back to `[primaryKey]`
 */
export const prepareFields = <T>(fields: unknown, primaryKey: string): T => {
  const defaultValue = [primaryKey] as T

  if (Array.isArray(fields)) {
    return fields.length ? (fields as T) : defaultValue
  }

  if (isString(fields)) {
    return fields.length ? ([fields] as T) : defaultValue
  }

  /*
  |--------------------------------------------------------------------------
  | Indexed-object shim
  |--------------------------------------------------------------------------
  |
  | Query-string parsers (e.g. qs with a low arrayLimit) encode arrays as
  | plain objects with numeric string keys: {"0":"id","1":"status",...}.
  | Detect that shape, sort by key, keep only string values, and return the
  | resulting array so downstream Joi validation sees a normal string array.
  |
  */
  if (isObject(fields)) {
    const entries = Object.entries(fields)
    if (entries.length > 0 && entries.every(([key]) => /^\d+$/.test(key))) {
      const sorted = entries
        .sort(([a], [b]) => Number.parseInt(a, 10) - Number.parseInt(b, 10))
        .map(([, value]) => value)
        .filter((value): value is string => isString(value))
      if (sorted.length > 0) {
        return sorted as unknown as T
      }
    }
  }

  return defaultValue
}

/**
 * Strips undefined values from an object, returning a new object with only defined values.
 *
 * @param obj - The object to strip undefined values from
 * @returns A new object with undefined values removed
 */
export const stripUndefinedValuesFromObject = <T extends Record<string, any>>(obj: T): T => {
  return Object.fromEntries(Object.entries(obj).filter(([_, value]) => value !== undefined)) as T
}

/**
 * Formats a syntax error message for a specific line and column in a source string.
 *
 * @param source - The source string to search for the error
 * @param line - The line number where the error occurred (1-based)
 * @param column - The column number where the error occurred (1-based)
 * @param offset - The character offset where the error occurred (0-based)
 * @returns A formatted error message
 */
export const formatSyntaxError = (
  source: string,
  line: number,
  column: number,
  offset: number
): string => {
  const lines = source.split('\n')
  const errorLine = lines[line - 1] // Convert to 0-based index

  if (!errorLine) {
    return `Error at line ${line}, column ${column} (offset ${offset})\nLine not found in source.`
  }

  // Create the error indicator line with spaces and a caret
  const indicator = ' '.repeat(Math.max(0, column - 1)) + '^'

  // Build the formatted error message
  let result = `Syntax Error at line ${line}, column ${column} (offset ${offset}):\n\n`

  // Show context: previous line (if exists)
  if (line > 1 && lines[line - 2]) {
    result += `${(line - 1).toString().padStart(3)} | ${lines[line - 2]}\n`
  }

  // Show the error line with line number
  result += `${line.toString().padStart(3)} | ${errorLine}\n`

  // Show the indicator line
  result += `${' '.repeat(3)} | ${indicator}\n`

  // Show context: next line (if exists)
  if (line < lines.length && lines[line]) {
    result += `${(line + 1).toString().padStart(3)} | ${lines[line]}\n`
  }

  return result
}
